// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
// Global
import { useSitecoreContext, Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useRef, useState } from 'react';

// Lib
import { ComponentProps } from 'lib/component-props';
import { DartPages } from 'lib/templates/Project.Dart.model';
import { DartComponents } from 'lib/templates/Feature.Dart.model';
import { BaseFields } from 'lib/templates/Foundation.Dart.model';

// Local
import CtaBorderAnimation from 'helpers/CTA/CtaBorderAnimation';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import Search from 'src/widgets/Search';
import { useRouter } from 'next/router';
import { useTheme } from 'lib/context/ThemeContext';
import { tailwindVariants } from './HeaderDesktopTailwind';
import Link from 'next/link';
import { useI18n } from 'next-localization';
import { Result } from '../ProductInformation/ProductInformation';

export type NameDefault = {
  nameDefault?: { jsonValue: Field<string> };
};

export type Countrylist = DartComponents.Navigation.RegionJson &
  NameDefault &
  DartComponents.Navigation.CountryJson;

export type FeaturedLinks = BaseFields.CtaJson;

export type HeaderDesktopProps = ComponentProps &
  DartComponents.HeaderJson & {
    staticProps?: {
      header?: DartComponents.HeaderJson;
    };
  } & { registeredResult?: Result };

export type MainNavigations = DartComponents.Navigation.SecondaryNavigationJson &
  DartComponents.Navigation.PrimaryHeaderNavigationJson &
  BaseFields.FeaturedJson;

export type SecondaryNavigations = DartComponents.Navigation.SecondaryNavigationJson;

export type SearchPage = DartPages.SearchPage;

const HeaderDesktop = (props: HeaderDesktopProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { themeName } = useTheme();
  const router = useRouter();
  const buttonRef = useRef<HTMLDivElement>(null);
  const omniBoxRef = useRef<HTMLDivElement | null>(null);
  const i18n = useI18n();
  const i18n_CountrySelectorSearchPlaceholder = i18n.t('CountrySelectorSearchPlaceholder')
    ? i18n.t('CountrySelectorSearchPlaceholder')
    : 'Search Locale';
  const i18n_CountrySelectorNoResultFound = i18n.t('CountrySelectorNoResultFound')
    ? i18n.t('CountrySelectorNoResultFound')
    : 'No results found';

  const rtlAlignment = sitecoreContext?.enableRTL === '1' ? 'rtl' : 'ltr';

  const omniBoxPresent = props?.staticProps?.header?.omniBox?.jsonValue?.value;

  const {
    enableSearch,
    logoImage,
    logoUrl,
    mainNavigations,
    registeredNavigation,
    regions,
    title,
    autoSuggestionItems,
    searchPlaceholderText,
    resultSuggestionItems,
  } = props?.staticProps?.header || {};

  const countryListItems = regions?.targetItems.flatMap((region: Countrylist) => {
    return region?.countries?.targetItems;
  });

  const navLength = (mainNavigations?.targetItems?.length as number) > 4;

  /* It is checking the lengths of `regions.targetItems`, `countryListItems`, and then
  determining the value of `languageLength` based on whether either `regions.targetItems` 
  or `countryListItems` has more than 1 item in that case only display dropdown list,
  otherwise don't display arrow and dropdown list of languages. */
  const regionsLength = regions?.targetItems?.length as number;
  const countryListItemsLength = countryListItems?.length as number;
  const languageLength = regionsLength > 1 || countryListItemsLength > 1;

  const {
    base,
    countryDropDownIcon,
    countryInformation,
    countryItemLink,
    countryList,
    countryListBottom,
    countryListCol,
    countryListCols,
    countryListHeading,
    countryListInnerWrapper,
    countryListItemLinkleft,
    countryListItemLinkleftInfo,
    countryListItemLinkleftInfoSubTitle,
    countryListItemLinkleftInfoTitle,
    countryListRight,
    countryListRightIcon,
    countryListTop,
    countryListWrapper,
    countryName,
    countrySelection,
    countryWrapper,
    countryWrapperInner,
    currentCountry,
    currentCountryLanguage,
    currentCountryName,
    omniBoxListWrapper,
    omniBoxInputWrapper,
    omniBoxInput,
    omniBoxDropdownList,
    omniBoxListItem,
    omniBoxListItemNoResult,
    featuredContentImage,
    featuredContentImageWrapper,
    featuredContentLinkItem,
    featuredContentLinks,
    featuredContentLinksWrapper,
    featuredContentTitle,
    featuredContentTitleWrapper,
    featuredContentWrapper,
    flagIconWrapper,
    headerLeft,
    headerRight,
    headerWrapper,
    logo,
    logoPlaceholder,
    logoWrapper,
    navigation,
    navigationInner,
    primaryNavigation,
    secondaryDropdownItem,
    secondaryDropdownItemLink,
    secondaryDropdownListItem,
    secondaryDropdownWrapper,
    secondaryNavigation,
    secondaryNavigationItemsWrapper,
    secondaryNavigationListItemLinkIcon,
    secondaryNavigationListItemLinkIconWrapper,
    secondaryNavigationListItemWrapper,
    secondaryNavigationWrapper,
  } = tailwindVariants({
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
    rtlAlignment: rtlAlignment as 'ltr' | 'rtl',
    navLength,
    languageLength,
  });

  const { componentName, dataSource } = props?.rendering || {};

  const SearchProps = {
    autoSuggestionItems: autoSuggestionItems?.jsonValue.value || 5,
    resultSuggestionItems: resultSuggestionItems?.jsonValue.value || 5,
    searchPlaceholderText: searchPlaceholderText?.jsonValue?.value,
    defaultValue: '',
    mainNavigationsLength: mainNavigations?.targetItems?.length as number,
  };

  // Check if the product is registered or not
  const isRegisteredProduct = props?.registeredResult?.isRegistered;

  // Get the registered navigation if the product is registered, otherwise use the main navigations
  const displayNavigation =
    isRegisteredProduct && (registeredNavigation?.targetItems?.length ?? 0) > 0
      ? registeredNavigation?.targetItems
      : mainNavigations?.targetItems;

  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [matchingCountry, setMatchingCountry] = useState<Countrylist | null | undefined>();
  const [pathname, setPathname] = useState(`${router?.locale}/${router?.asPath}`);
  const [openCountryWrapper, setOpenCountryWrapper] = useState<boolean>(false);
  const [openOmniBoxWrapper, setOpenOmniBoxWrapper] = useState<boolean>(false);
  const [searchOmniBoxLanguage, setSearchOmniBoxLanguage] = useState('');
  const [isInputFocused, setInputFocused] = useState(false);

  // Determine if the search should be shown based on the enableSearch field and whether the product is registered
  const shouldShowSearch = enableSearch?.jsonValue?.value && !isRegisteredProduct;

  useEffect(() => {
    router && setPathname(`/${router.locale}${router.asPath}`);
    setHoveredItem(null);
    setOpenCountryWrapper(false);
    setOpenOmniBoxWrapper(false);
  }, [router]);

  useEffect(() => {
    if (!regions) return;

    const foundCountry = regions?.targetItems
      .flatMap((region: Countrylist) => {
        return region?.countries?.targetItems;
      })
      .find(
        (country?: Countrylist) =>
          country?.languageCode?.jsonValue?.value === sitecoreContext.language
      );

    setMatchingCountry(foundCountry);
  }, [props, regions, sitecoreContext]);

  const handleMouseEnter = (index: number) => {
    setHoveredItem(index);
    setOpenCountryWrapper(false);
    setOpenOmniBoxWrapper(false);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const showCountryWrapper = () => {
    setOpenOmniBoxWrapper(!openOmniBoxWrapper);
    setOpenCountryWrapper(!openCountryWrapper);
    setInputFocused(false);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLElement>,
    secondaryNavExists: boolean
  ) => {
    if (e.key === 'Enter') {
      if (secondaryNavExists && e?.preventDefault) e.preventDefault();
      setHoveredItem(hoveredItem === index ? null : index);
      if (hoveredItem !== index) {
        setOpenCountryWrapper(false);
        setOpenOmniBoxWrapper(false);
      }
    }
  };

  // Create a list of locales with labels and links - OmniBox Language Selector
  // Each item in the list contains a label and a link
  // The label is a combination of country name and language name
  // The link is the href from the country link
  const locales = countryListItems
    ?.map((item) => ({
      label: `${item.name?.jsonValue?.value || item?.nameDefault?.jsonValue?.value || ''} - ${
        item.languageName?.jsonValue?.value || ''
      }`,
      link: item.link?.jsonValue?.value?.href || '#',
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Filter the locales based on the search input
  // This will filter the locales based on the search input in the OmniBox
  const filteredLocales = locales?.filter((locale) =>
    locale.label.toLowerCase().includes(searchOmniBoxLanguage.toLowerCase())
  );

  // If no locales match the search, show a "No results found" message
  const isDropdownOpen = omniBoxPresent ? openOmniBoxWrapper : openCountryWrapper;

  // Handle click outside to close the dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        openOmniBoxWrapper &&
        omniBoxRef.current &&
        !omniBoxRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpenOmniBoxWrapper(false);
        setInputFocused(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openOmniBoxWrapper]);

  return (
    <div className={base()} data-component="authorable/landmarks/header">
      <div className={headerWrapper()}>
        <div className={headerLeft()}>
          <div className={logoWrapper()}>
            <div className={logo()}>
              {pathname !== '/' ? (
                <LinkWrapper
                  aria-label="Link to homepage"
                  field={logoUrl?.jsonValue}
                  gtmEvent={{
                    event: 'navigation_click',
                    'gtm.element.dataset.gtmDatasourceId': dataSource,
                    'gtm.element.dataset.gtmComponentName': componentName,
                  }}
                  title="Link to homepage"
                >
                  <ImageWrapper field={logoImage?.jsonValue} />
                </LinkWrapper>
              ) : (
                <ImageWrapper field={logoImage?.jsonValue} />
              )}
            </div>
          </div>
          <nav className={navigation()}>
            <div className={navigationInner()}>
              <ul className={primaryNavigation()}>
                {displayNavigation?.map((ListItem: MainNavigations, index: number) => {
                  const {
                    primaryNavigationListItem,
                    primaryNavigationListItemArrowDropDown,
                    primaryNavigationListItemLink,
                    primaryNavigationListItemLinkWrapper,
                    primaryNavigationListItemWrapper,
                  } = tailwindVariants({
                    hasHref:
                      (ListItem?.navigationLink?.jsonValue?.value?.href &&
                        ListItem?.navigationLink?.jsonValue?.value?.href?.length < 0) ||
                      false,
                    isActive:
                      (ListItem?.navigationLink?.jsonValue?.value?.href &&
                        ListItem?.navigationLink?.jsonValue?.value?.href === pathname) ||
                      false,
                    isDropdown: ListItem?.isDropdown?.jsonValue?.value,
                    /* eslint-disable  @typescript-eslint/ban-ts-comment */
                    // @ts-ignore
                    brand: themeName as string,
                  });

                  return (
                    <li
                      className={primaryNavigationListItemWrapper()}
                      key={index}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                      onKeyDown={(e) =>
                        handleKeyDown(
                          index,
                          e,
                          ListItem?.secondaryNavigations?.targetItems?.length !== 0
                        )
                      }
                    >
                      <div className={primaryNavigationListItem()}>
                        <div
                          className={primaryNavigationListItemLinkWrapper()}
                          tabIndex={
                            ListItem?.secondaryNavigations?.targetItems?.length !== 0 ? 0 : -1
                          }
                        >
                          {ListItem?.navigationLink?.jsonValue?.value?.href ? (
                            <LinkWrapper
                              className={primaryNavigationListItemLink()}
                              field={ListItem?.navigationLink?.jsonValue}
                              gtmEvent={{
                                event: 'navigation_click',
                                type: 'header',
                                'gtm.element.dataset.gtmLinkName':
                                  ListItem?.navigationLink?.jsonValue?.value.text,
                                'gtm.element.dataset.gtmLinkUrl':
                                  ListItem?.navigationLink?.jsonValue?.value?.href,
                                'gtm.element.dataset.gtmDatasourceId': dataSource,
                                'gtm.element.dataset.gtmComponentName': componentName,
                              }}
                              tabIndex={
                                ListItem?.secondaryNavigations?.targetItems?.length !== 0 ? -1 : 0
                              }
                              aria-haspopup={
                                ListItem?.secondaryNavigations?.targetItems?.length !== 0 &&
                                hoveredItem === index
                                  ? 'true'
                                  : 'false'
                              }
                            />
                          ) : (
                            <Text
                              aria-label={ListItem?.navigationTitle?.jsonValue.value}
                              className={primaryNavigationListItemLink()}
                              encode={false}
                              field={ListItem?.navigationTitle?.jsonValue}
                              onFocus={() => {
                                handleMouseEnter(index);
                              }}
                              tag="span"
                              tabIndex={-1}
                            />
                          )}
                          {ListItem?.secondaryNavigations?.targetItems?.length !== 0 && (
                            <GoogleMaterialSymbol
                              className={primaryNavigationListItemArrowDropDown()}
                              icon="arrow_drop_down"
                            />
                          )}
                        </div>
                      </div>
                      {hoveredItem === index &&
                        ListItem?.secondaryNavigations?.targetItems?.length !== 0 &&
                        !openCountryWrapper &&
                        !openOmniBoxWrapper &&
                        (!ListItem?.isDropdown?.jsonValue?.value ? (
                          // Mega Menu
                          <div className={secondaryNavigationWrapper()}>
                            <ul className={secondaryNavigation()}>
                              {ListItem?.secondaryNavigations?.targetItems?.map(
                                (ListIteminner: SecondaryNavigations, index: number) => {
                                  const {
                                    secondaryNavigationListItemLinkIcon,
                                    secondaryNavigationListItemTitleLink,
                                    secondaryNavigationListItemTitleLinkWrapper,
                                  } = tailwindVariants({
                                    isActive:
                                      (ListIteminner?.navigationLink?.jsonValue?.value?.href &&
                                        ListIteminner?.navigationLink?.jsonValue?.value?.href ===
                                          pathname) ||
                                      false,
                                    isDropdown: ListItem?.isDropdown?.jsonValue?.value,
                                    /* eslint-disable  @typescript-eslint/ban-ts-comment */
                                    // @ts-ignore
                                    brand: themeName as string,
                                  });

                                  return (
                                    <li
                                      key={index}
                                      className={secondaryNavigationListItemWrapper()}
                                    >
                                      {ListIteminner?.navigationLink?.jsonValue?.value?.href ? (
                                        <div
                                          className={secondaryNavigationListItemTitleLinkWrapper()}
                                        >
                                          <LinkWrapper
                                            className={secondaryNavigationListItemTitleLink()}
                                            field={ListIteminner?.navigationLink?.jsonValue}
                                            gtmEvent={{
                                              event: 'navigation_click',
                                              type: 'header',
                                              'gtm.element.dataset.gtmLinkName':
                                                ListIteminner?.navigationLink?.jsonValue?.value
                                                  .text,
                                              'gtm.element.dataset.gtmLinkUrl':
                                                ListIteminner?.navigationLink?.jsonValue?.value
                                                  ?.href,
                                              'gtm.element.dataset.gtmDatasourceId': dataSource,
                                              'gtm.element.dataset.gtmComponentName': componentName,
                                            }}
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.stopPropagation();
                                              }
                                            }}
                                          >
                                            <div
                                              className={secondaryNavigationListItemLinkIconWrapper()}
                                            >
                                              {/* Added this as a wrapper placeholder as parent div of this
                                          only renders on hover of navItem and each time
                                          GoogleMaterialSymbol  */}
                                              <GoogleMaterialSymbol
                                                className={secondaryNavigationListItemLinkIcon()}
                                                icon="chevron_right"
                                              />
                                            </div>
                                          </LinkWrapper>
                                        </div>
                                      ) : (
                                        <div
                                          className={secondaryNavigationListItemTitleLinkWrapper()}
                                        >
                                          <Text
                                            aria-label={
                                              ListIteminner?.navigationLink?.jsonValue?.value
                                            }
                                            className={secondaryNavigationListItemTitleLink()}
                                            encode={false}
                                            field={{
                                              value:
                                                ListIteminner?.navigationTitle?.jsonValue?.value,
                                            }}
                                            onFocus={() => {
                                              handleMouseEnter(index);
                                            }}
                                            tag="span"
                                            tabIndex={-1}
                                          />
                                        </div>
                                      )}
                                      <ul className={secondaryNavigationItemsWrapper()}>
                                        {ListIteminner?.navigations?.targetItems?.map(
                                          (SecondaryItems: SecondaryNavigations, index: number) => {
                                            const {
                                              secondaryNavigationItemLink,
                                              secondaryNavigationListItemLinkIcon,
                                              secondaryNavigationListItemLinkIconWrapper,
                                              secondaryNavigationItem,
                                            } = tailwindVariants({
                                              isActive:
                                                (SecondaryItems?.navigationLink?.jsonValue?.value
                                                  ?.href &&
                                                  SecondaryItems?.navigationLink?.jsonValue?.value
                                                    ?.href === pathname) ||
                                                false,
                                              isDropdown: ListItem?.isDropdown?.jsonValue?.value,
                                              /* eslint-disable  @typescript-eslint/ban-ts-comment */
                                              // @ts-ignore
                                              brand: themeName as string,
                                            });

                                            return SecondaryItems?.navigationLink?.jsonValue?.value
                                              ?.href ? (
                                              <li key={index} className={secondaryNavigationItem()}>
                                                <LinkWrapper
                                                  className={secondaryNavigationItemLink()}
                                                  field={SecondaryItems?.navigationLink?.jsonValue}
                                                  gtmEvent={{
                                                    event: 'navigation_click',
                                                    type: 'header',
                                                    'gtm.element.dataset.gtmLinkName':
                                                      SecondaryItems?.navigationLink?.jsonValue
                                                        ?.value.text,
                                                    'gtm.element.dataset.gtmLinkUrl':
                                                      SecondaryItems?.navigationLink?.jsonValue
                                                        ?.value?.href,
                                                    'gtm.element.dataset.gtmDatasourceId':
                                                      dataSource,
                                                    'gtm.element.dataset.gtmComponentName':
                                                      componentName,
                                                  }}
                                                  tabIndex={0}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      e.stopPropagation();
                                                    }
                                                  }}
                                                >
                                                  <div
                                                    className={secondaryNavigationListItemLinkIconWrapper()}
                                                  >
                                                    <GoogleMaterialSymbol
                                                      className={secondaryNavigationListItemLinkIcon()}
                                                      icon="chevron_right"
                                                      variant="outlined"
                                                    />
                                                  </div>
                                                </LinkWrapper>
                                              </li>
                                            ) : (
                                              <li key={index} className={secondaryNavigationItem()}>
                                                <Text
                                                  aria-label={
                                                    SecondaryItems?.navigationTitle?.jsonValue
                                                      ?.value
                                                  }
                                                  className={secondaryNavigationItemLink()}
                                                  encode={false}
                                                  field={{
                                                    value:
                                                      SecondaryItems?.navigationTitle?.jsonValue
                                                        ?.value,
                                                  }}
                                                  onFocus={() => {
                                                    handleMouseEnter(index);
                                                  }}
                                                  tag="span"
                                                  tabIndex={-1}
                                                />
                                              </li>
                                            );
                                          }
                                        )}
                                      </ul>
                                    </li>
                                  );
                                }
                              )}
                            </ul>
                            {ListItem?.featuredImage?.jsonValue?.value?.src && (
                              <div className={featuredContentWrapper()}>
                                <div className={featuredContentTitleWrapper()}>
                                  <Text
                                    className={featuredContentTitle()}
                                    encode={false}
                                    field={{ value: ListItem?.featuredTitle?.jsonValue?.value }}
                                    tag="p"
                                  />
                                </div>
                                <div className={featuredContentImageWrapper()}>
                                  <ImageWrapper
                                    field={ListItem?.featuredImage?.jsonValue}
                                    className={featuredContentImage()}
                                  />
                                </div>
                                <div className={featuredContentLinksWrapper()}>
                                  <ul className={featuredContentLinks()}>
                                    {ListItem?.featuredLinks?.targetItems?.map(
                                      (featuredLinkItem: FeaturedLinks, index: number) => {
                                        return (
                                          <React.Fragment key={index}>
                                            {featuredLinkItem?.ctaLink?.jsonValue?.value?.href && (
                                              <li className={featuredContentLinkItem()}>
                                                {featuredLinkItem?.ctaLink && (
                                                  <CtaBorderAnimation
                                                    fields={featuredLinkItem?.ctaLink?.jsonValue}
                                                  />
                                                )}
                                              </li>
                                            )}
                                          </React.Fragment>
                                        );
                                      }
                                    )}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Dropdown Menu
                          <div className={secondaryDropdownWrapper()}>
                            <ul className={secondaryDropdownListItem()}>
                              {ListItem?.secondaryNavigations?.targetItems?.map(
                                (ListIteminner: SecondaryNavigations, index: number) => {
                                  return ListIteminner?.navigationLink?.jsonValue?.value?.href ? (
                                    <li key={index}>
                                      <div className={secondaryDropdownItem()}>
                                        <LinkWrapper
                                          className={secondaryDropdownItemLink()}
                                          field={ListIteminner?.navigationLink?.jsonValue}
                                          gtmEvent={{
                                            event: 'navigation_click',
                                            type: 'header',
                                            'gtm.element.dataset.gtmLinkName':
                                              ListIteminner?.navigationLink?.jsonValue?.value.text,
                                            'gtm.element.dataset.gtmLinkUrl':
                                              ListIteminner?.navigationLink?.jsonValue?.value?.href,
                                            'gtm.element.dataset.gtmDatasourceId': dataSource,
                                            'gtm.element.dataset.gtmComponentName': componentName,
                                          }}
                                          tabIndex={0}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.stopPropagation();
                                            }
                                          }}
                                          onBlur={() => {
                                            const isLastLink =
                                              index ===
                                              (ListItem?.secondaryNavigations?.targetItems
                                                ?.length ?? 0) -
                                                1;
                                            if (isLastLink) {
                                              handleMouseLeave();
                                            }
                                          }}
                                        >
                                          <div
                                            className={secondaryNavigationListItemLinkIconWrapper()}
                                          >
                                            <GoogleMaterialSymbol
                                              className={secondaryNavigationListItemLinkIcon()}
                                              icon="chevron_right"
                                            />
                                          </div>
                                        </LinkWrapper>
                                      </div>
                                    </li>
                                  ) : (
                                    <li key={index}>
                                      <div className={secondaryDropdownItem()}>
                                        <Text
                                          aria-label={
                                            ListIteminner?.navigationTitle?.jsonValue?.value
                                          }
                                          className={secondaryDropdownItemLink()}
                                          encode={false}
                                          field={{
                                            value: ListIteminner?.navigationTitle?.jsonValue?.value,
                                          }}
                                          onFocus={() => {
                                            handleMouseEnter(index);
                                          }}
                                          tag="span"
                                          tabIndex={-1}
                                        />
                                      </div>
                                    </li>
                                  );
                                }
                              )}
                            </ul>
                          </div>
                        ))}
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </div>

        <div className={headerRight()}>
          {/* Search */}
          {shouldShowSearch && <Search rfkId={'rfkid_11'} {...SearchProps} />}

          {/* Language Switcher */}
          {/* If there are missing content for language then hide countryWrapper */}
          {matchingCountry?.languageName?.jsonValue?.value && (
            <div className={countryWrapper()}>
              {/* If the regions have only 1 language added then hide dropdown */}
              {languageLength ? (
                <div
                  className={countryWrapperInner()}
                  role="button"
                  ref={buttonRef}
                  tabIndex={0}
                  aria-label={`Current Country and Language - ${
                    matchingCountry?.name?.jsonValue?.value
                      ? matchingCountry?.name?.jsonValue?.value
                      : matchingCountry?.nameDefault?.jsonValue?.value
                  } ${matchingCountry?.languageName?.jsonValue?.value}`}
                  onClick={showCountryWrapper}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      showCountryWrapper();
                    }
                  }}
                >
                  <div className={flagIconWrapper()}>
                    {matchingCountry?.flagIcon?.jsonValue?.value?.src ? (
                      <ImageWrapper field={matchingCountry?.flagIcon?.jsonValue} />
                    ) : (
                      <div className={logoPlaceholder()}></div>
                    )}
                  </div>
                  <div className={currentCountry()}>
                    <div className={countryInformation()}>
                      <div className={currentCountryName()}>
                        {matchingCountry?.name?.jsonValue?.value
                          ? matchingCountry?.name?.jsonValue?.value
                          : matchingCountry?.nameDefault?.jsonValue?.value}
                      </div>
                      <div className={currentCountryLanguage()}>
                        {matchingCountry?.languageName?.jsonValue?.value}
                      </div>
                    </div>
                    <GoogleMaterialSymbol
                      className={countryDropDownIcon()}
                      icon={isDropdownOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
                    />
                  </div>
                </div>
              ) : (
                <div className={countryWrapperInner()}>
                  <div className={flagIconWrapper()}>
                    {matchingCountry?.flagIcon?.jsonValue?.value?.src ? (
                      <ImageWrapper field={matchingCountry?.flagIcon?.jsonValue} />
                    ) : (
                      <div className={logoPlaceholder()}></div>
                    )}
                  </div>
                  <div className={currentCountry()}>
                    <div className={countryInformation()}>
                      <div className={currentCountryName()}>
                        {matchingCountry?.name?.jsonValue?.value
                          ? matchingCountry?.name?.jsonValue?.value
                          : matchingCountry?.nameDefault?.jsonValue?.value}
                      </div>
                      <div className={currentCountryLanguage()}>
                        {matchingCountry?.languageName?.jsonValue?.value}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {omniBoxPresent ? (
                <>
                  {openOmniBoxWrapper && (
                    <div ref={omniBoxRef} className={omniBoxListWrapper()}>
                      {/* Input */}
                      <div
                        className={omniBoxInputWrapper({
                          isInputFocused: isInputFocused,
                        })}
                      >
                        <input
                          type="text"
                          placeholder={i18n_CountrySelectorSearchPlaceholder}
                          value={searchOmniBoxLanguage}
                          onClick={() => !isInputFocused && setInputFocused(true)}
                          onChange={(e) => setSearchOmniBoxLanguage(e.target.value)}
                          className={omniBoxInput({ isInputFocused: isInputFocused })}
                          aria-label="Search Locale"
                        />
                        {searchOmniBoxLanguage && (
                          <button onClick={() => setSearchOmniBoxLanguage('')}>
                            <GoogleMaterialSymbol icon={'close'} variant="outlined" />
                          </button>
                        )}
                      </div>

                      {/* Dropdown List */}
                      <ul className={omniBoxDropdownList()} role="listbox">
                        {(filteredLocales ?? []).length > 0 ? (
                          filteredLocales?.map((locale, index) => (
                            <li key={index}>
                              <Link
                                aria-label={locale.label}
                                className={omniBoxListItem()}
                                target="_blank"
                                rel="noopener noreferrer"
                                href={locale.link}
                              >
                                {locale.label}
                              </Link>
                            </li>
                          ))
                        ) : (
                          <li className={omniBoxListItemNoResult()}>
                            {i18n_CountrySelectorNoResultFound}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {openCountryWrapper && (
                    <div className={countryListWrapper()}>
                      <div className={countryListInnerWrapper()}>
                        <div className={countryListTop()}>
                          <div className={countryListHeading()}>{title?.jsonValue?.value}</div>
                        </div>
                        <div className={countryListBottom()}>
                          {regions?.targetItems?.map(
                            (country: Countrylist & NameDefault, index: number) => {
                              return (
                                <React.Fragment key={index}>
                                  {country?.countries?.targetItems?.length !== 0 &&
                                    (country?.name?.jsonValue ||
                                      country?.nameDefault?.jsonValue) && (
                                      <div className={countryListCols()}>
                                        <div className={countryListCol()}>
                                          <Text
                                            className={countryName()}
                                            encode={false}
                                            field={
                                              country?.name?.jsonValue
                                                ? country?.name?.jsonValue
                                                : country?.nameDefault?.jsonValue
                                            }
                                            tag="p"
                                          />
                                        </div>
                                        <div className={countryList()}>
                                          {country?.countries?.targetItems?.map(
                                            (countryItem: Countrylist, index: number) => {
                                              const matchingCountryItemName = matchingCountry?.name
                                                ?.jsonValue
                                                ? matchingCountry?.name?.jsonValue
                                                : matchingCountry?.nameDefault?.jsonValue;
                                              const countryItemName = countryItem?.name?.jsonValue
                                                ? countryItem?.name?.jsonValue
                                                : countryItem?.nameDefault?.jsonValue;
                                              const isMatchingCountry =
                                                matchingCountryItemName?.value ===
                                                  countryItemName?.value &&
                                                matchingCountry?.languageName?.jsonValue?.value ===
                                                  countryItem?.languageName?.jsonValue?.value;

                                              const { countryItemLinkWrapper } = tailwindVariants({
                                                matchingCountry: isMatchingCountry,
                                              });

                                              // use interanl links to conrol languge switching. external links just redirect to sites we haven't built yet.
                                              const linkField = countryItem?.link?.jsonValue;
                                              if (linkField?.value?.linktype == 'internal') {
                                                linkField.value.href = '/';
                                              }

                                              return (
                                                <>
                                                  {countryItem?.link?.jsonValue?.value?.text && (
                                                    <div
                                                      className={countryItemLinkWrapper()}
                                                      key={index}
                                                    >
                                                      <LinkWrapper
                                                        aria-label={`Set Country and Language to ${countryItemName?.value} ${countryItem?.languageName?.jsonValue?.value}`}
                                                        className={countryItemLink()}
                                                        field={countryItem?.link?.jsonValue}
                                                        showLinkTextWithChildrenPresent={false}
                                                        target={
                                                          countryItem?.link?.jsonValue?.value
                                                            ?.target
                                                        }
                                                        locale={countryItem?.languageCode?.jsonValue?.value?.toLocaleLowerCase()}
                                                      >
                                                        <div className={countryListItemLinkleft()}>
                                                          {countryItem?.flagIcon?.jsonValue?.value
                                                            ?.src && (
                                                            <>
                                                              <ImageWrapper
                                                                field={
                                                                  countryItem?.flagIcon?.jsonValue
                                                                }
                                                              />
                                                            </>
                                                          )}
                                                          <div
                                                            className={countryListItemLinkleftInfo()}
                                                          >
                                                            <Text
                                                              className={countryListItemLinkleftInfoTitle()}
                                                              encode={false}
                                                              field={countryItemName}
                                                              tag="p"
                                                            />
                                                            <span
                                                              className={countryListItemLinkleftInfoSubTitle()}
                                                            >
                                                              {
                                                                countryItem?.languageName?.jsonValue
                                                                  ?.value
                                                              }
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className={countryListRight()}>
                                                          <div className={countrySelection()}>
                                                            <>
                                                              <GoogleMaterialSymbol
                                                                className={`${
                                                                  isMatchingCountry
                                                                    ? 'fill-color-system-success-400'
                                                                    : countryListRightIcon()
                                                                } `}
                                                                icon="done"
                                                              />
                                                            </>
                                                          </div>
                                                        </div>
                                                      </LinkWrapper>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            }
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </React.Fragment>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderDesktop;
